import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSubscription } from '@hooks/useSubscription';
import { Subscription, TeacherProfile } from 'types/collections';

import { DailyAvailability } from '@components/DaySelectionPanel';
import { showToast } from '@components/ToastContainer';

import { initializeSelectedDays } from '@utils/helpers';

type SaveNotificationMode = 'created' | 'updated';

interface SaveNotificationSuccessData {
  mode: SaveNotificationMode;
  selectedDays: DailyAvailability;
}

/**
 * Custom hook for managing subscription operations in TeacherListing
 * @param listingId - The ID of the school listing
 * @param currentSubscription - Current subscription if it exists
 * @param teacher - Teacher profile data
 * @returns Subscription management state and handlers
 */
export const useSubscriptionManagement = (
  listingId: string,
  currentSubscription: Subscription | undefined,
  teacher: TeacherProfile | undefined,
  onSaveSuccess?: (data: SaveNotificationSuccessData) => void,
) => {
  const { t } = useTranslation('common');
  const { createSubscription, deleteSubscription, updateSubscription } = useSubscription();

  const [selectedDays, setSelectedDays] = useState<DailyAvailability>({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
  });

  // Initialize selected days based on subscription or teacher general availability
  useEffect(() => {
    setSelectedDays(initializeSelectedDays(currentSubscription, teacher));
  }, [teacher, currentSubscription]);

  const handleToggleDay = useCallback((day: keyof DailyAvailability) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  }, []);

  const handleSaveAvailability = useCallback(async () => {
    const daysToSave = selectedDays;

    // Always save the subscription state, even if all days are false
    // This preserves the teacher's explicit choice to receive no notifications
    if (currentSubscription) {
      // Update existing subscription with current selection (including all false)
      updateSubscription(
        {
          subscriptionId: currentSubscription.id,
          selectedDays: daysToSave,
        },
        {
          onSuccess: () => {
            onSaveSuccess?.({ mode: 'updated', selectedDays: daysToSave });
          },
        },
      );
    } else {
      // Create new subscription with current selection (including all false)
      createSubscription(
        {
          listing_id: listingId,
          selectedDays: daysToSave,
        },
        {
          onSuccess: () => {
            onSaveSuccess?.({ mode: 'created', selectedDays: daysToSave });
          },
        },
      );
    }
  }, [
    selectedDays,
    currentSubscription,
    listingId,
    updateSubscription,
    createSubscription,
    onSaveSuccess,
  ]);

  const handleUnsubscribe = useCallback(() => {
    if (!currentSubscription) return;
    deleteSubscription(currentSubscription.id, {
      onSuccess: () => {
        showToast('success', t('toast.success'), t('toast.successUnsubscribe'));
      },
      onError: (error) => alert(`Failed to unsubscribe: ${error.message}`),
    });
  }, [currentSubscription, deleteSubscription, t]);

  const resetToSavedState = useCallback(() => {
    // Reset selectedDays back to the saved subscription or teacher availability data
    setSelectedDays(initializeSelectedDays(currentSubscription, teacher));
  }, [currentSubscription, teacher]);

  return {
    selectedDays,
    handleToggleDay,
    handleSaveAvailability,
    handleUnsubscribe,
    resetToSavedState,
    setSelectedDays,
  };
};
